import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import * as z from "zod/v4";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  eachDayOfInterval,
  endOfToday,
  format,
  startOfToday,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import {
  Calendar,
  Clock,
  FileText,
  Flag,
  Video,
  VideoIcon,
  Volume2,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DateRange } from "react-day-picker";
import type { ChartConfig } from "@/components/ui/chart";

import type { MeetingBot } from "@/lib/queries/meetings";
import type { Calendar as LinkedCalendar } from "@/lib/queries/calendars";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/http";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip as UiTooltip,
  TooltipContent as UiTooltipContent,
  TooltipProvider as UiTooltipProvider,
  TooltipTrigger as UiTooltipTrigger,
} from "@/components/ui/tooltip";
import { meetingsListQueryOptions } from "@/lib/queries/meetings";
import { calendarsQueryOptions } from "@/lib/queries/calendars";
import { cn } from "@/lib/utils";
import { PII_BADGE_CLASSES } from "@/lib/pii-badge";
import {
  formatMeetingDuration,
  formatPlatform as formatMeetingPlatform,
  formatStatusLabel,
  getMeetingSeverity,
  getSeverityAccentClass,
  getSeverityLabel,
} from "@/routes/_app/meetings/-components/meetings-utils";
import {
  MeetingCalendarBadge,
  // MeetingPlatformBadge,
} from "@/routes/_app/meetings/-components/meeting-badges";
import Zoom from "@/components/icons/images/brand-zoom.svg?url";
import Google from "@/components/icons/images/google-meet.svg?url";

type ScheduledMeetingEntry = { meeting: MeetingBot; startsAt: string };

type TimeSeriesDataPoint = {
  timestamp: string;
  meeting_count: number;
  pii_count: number;
  ai_count: number;
  vexon_fake_count: number;
};

type TimeSeriesStats = {
  total_meeting_count: number;
  total_pii_count: number;
  total_ai_count: number;
  total_vexon_fake_count: number;
  time_series: Array<TimeSeriesDataPoint>;
};

type PiiSummaryItem = {
  entity_id: string;
  entity_name: string;
  critical_count: number;
  confidential_count: number;
  standard_count: number;
  total_count: number;
};

type PiiAnalyticsStats = {
  pii_summary: Array<PiiSummaryItem>;
  total_pii_count: number;
  total_critical: number;
  total_confidential: number;
  total_standard: number;
};

type RiskStats = {
  low_risk_meetings: number;
  medium_risk_meetings: number;
  high_risk_meetings: number;
  total_meetings: number;
};

const chartRangeSchema = z.discriminatedUnion("period", [
  z.object({
    period: z.enum(["all", "today", "week", "month"]),
  }),
  z.object({
    period: z.literal("custom"),
    from: z.coerce.date(),
    to: z.coerce.date(),
  }),
]);

type ChartRange = z.infer<typeof chartRangeSchema>;

const dashboardSearchSchema = z.object({
  chartRange: chartRangeSchema
    .default({ period: "all" })
    .catch({ period: "all" }),
});

type Frequency = "hour" | "day" | "month" | "year";

function getDateRangeAndFrequency(range: ChartRange): {
  from: Date;
  to: Date;
  frequency: Frequency;
} {
  const now = new Date();

  switch (range.period) {
    case "all":
      return {
        from: subMonths(now, 12),
        to: now,
        frequency: "month",
      };
    case "today":
      return {
        from: startOfToday(),
        to: endOfToday(),
        frequency: "hour",
      };
    case "week":
      return {
        from: subWeeks(now, 1),
        to: now,
        frequency: "day",
      };
    case "month":
      return {
        from: subMonths(now, 1),
        to: now,
        frequency: "day",
      };
    case "custom": {
      const diffInDays = Math.ceil(
        (range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60 * 24),
      );

      let frequency: Frequency;
      if (diffInDays <= 1) {
        frequency = "hour";
      } else if (diffInDays <= 60) {
        frequency = "day";
      } else if (diffInDays <= 730) {
        frequency = "month";
      } else {
        frequency = "year";
      }

      return {
        from: range.from,
        to: range.to,
        frequency,
      };
    }
  }
}

function timeSeriesStatsQueryOptions(range: ChartRange) {
  return queryOptions({
    queryKey: ["stats", "time_series_stats", range],
    queryFn() {
      const { from, to, frequency } = getDateRangeAndFrequency(range);
      const searchParams = new URLSearchParams();

      searchParams.set("start_time", from.toISOString());
      searchParams.set("end_time", to.toISOString());
      searchParams.set("frequency", frequency);

      return api.processing.raw
        .get("api/v1/stats/time_series_stats", { searchParams })
        .json<TimeSeriesStats>();
    },
  });
}

function piiAnalyticsStatsQueryOptions(range: ChartRange) {
  return queryOptions({
    queryKey: ["stats", "pii_analytics", range],
    queryFn() {
      const { from, to } = getDateRangeAndFrequency(range);
      const searchParams = new URLSearchParams();

      searchParams.set("start_date", from.toISOString());
      searchParams.set("end_date", to.toISOString());

      return api.processing.raw
        .get("api/v1/stats/pii-analytics/", { searchParams })
        .json<PiiAnalyticsStats>();
    },
  });
}

function riskStatsQueryOptions(range: ChartRange) {
  return queryOptions({
    queryKey: ["stats", "risk_stats", range],
    queryFn() {
      const { from, to } = getDateRangeAndFrequency(range);
      const searchParams = new URLSearchParams();

      searchParams.set("start_date", from.toISOString());
      searchParams.set("end_date", to.toISOString());

      return api.processing.raw
        .get("api/v1/stats/risk_stats", { searchParams })
        .json<RiskStats>();
    },
  });
}

export const Route = createFileRoute("/_app/")({
  component: RouteComponent,
  validateSearch: zodValidator(dashboardSearchSchema),
});

const chartConfig = {
  meeting_count: {
    label: "Meetings",
    color: "#4E5BA6",
  },
  vexon_fake_count: {
    label: "Deepfake",
    color: "#0BA5EC",
  },
  ai_count: {
    label: "AI Content",
    color: "#9B8AFB",
  },
  pii_count: {
    label: "PII Detected",
    color: "#E478FA",
  },
} satisfies ChartConfig;

const riskChartConfig = {
  low: { label: "Low risk", color: "#099250" },
  medium: { label: "Medium risk", color: "#F79009" },
  high: { label: "High risk", color: "#D92D20" },
} satisfies ChartConfig;

const piiCriticalityChartConfig = {
  standard: { label: "Normal", color: "#099250" },
  confidential: { label: "Confidential", color: "#F79009" },
  critical: { label: "Critical", color: "#D92D20" },
} satisfies ChartConfig;

const BAR_FULL_RADIUS = [4, 4, 4, 4] as unknown as number;
const BAR_TOP_RADIUS = [4, 4, 0, 0] as unknown as number;
const BAR_BOTTOM_RADIUS = [0, 0, 4, 4] as unknown as number;
const BAR_NO_RADIUS = 0;

function getPiiStackRadius(entry: {
  critical: number;
  confidential: number;
  standard: number;
}) {
  const isTopCritical = entry.critical > 0;
  const isTopConfidential = !isTopCritical && entry.confidential > 0;
  const isTopStandard =
    !isTopCritical && !isTopConfidential && entry.standard > 0;

  const isBottomStandard = entry.standard > 0;
  const isBottomConfidential = !isBottomStandard && entry.confidential > 0;
  const isBottomCritical =
    !isBottomStandard && !isBottomConfidential && entry.critical > 0;

  const radiusFor = (isTop: boolean, isBottom: boolean) => {
    if (isTop && isBottom) return BAR_FULL_RADIUS;
    if (isTop) return BAR_TOP_RADIUS;
    if (isBottom) return BAR_BOTTOM_RADIUS;
    return BAR_NO_RADIUS;
  };

  return {
    standard: radiusFor(isTopStandard, isBottomStandard),
    confidential: radiusFor(isTopConfidential, isBottomConfidential),
    critical: radiusFor(isTopCritical, isBottomCritical),
  };
}

function PiiBarTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    value?: number;
    payload?: { name?: string; fill?: string };
  }>;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const item = payload[0];
  const value = item.value ?? 0;
  const name = item.payload?.name ?? "";
  const color = item.payload?.fill ?? "#DD2590";

  return (
    <div className="rounded-md bg-[#0c111d] px-2.5 py-1.5 text-xs font-medium text-white shadow-lg">
      <div className="flex items-center gap-1.5">
        <span
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span>
          {value.toLocaleString()} {name.toLowerCase()}
        </span>
      </div>
    </div>
  );
}

function PiiBarTick({
  x,
  y,
  payload,
  angled,
}: {
  x?: number;
  y?: number;
  payload?: { value?: string };
  angled?: boolean;
}) {
  const label = payload?.value ?? "";

  if (angled) {
    return (
      <g transform={`translate(${x ?? 0},${(y ?? 0) + 8})`}>
        <text
          transform="rotate(-35)"
          textAnchor="end"
          fontSize={10}
          fill="#9ca3af"
        >
          {label}
        </text>
      </g>
    );
  }

  return (
    <g transform={`translate(${x ?? 0},${(y ?? 0) + 8})`}>
      <text textAnchor="middle" fontSize={11} fill="#9ca3af">
        {label}
      </text>
    </g>
  );
}

const formatPlatform = (platform: string): string => {
  switch (platform.toLowerCase()) {
    case "google":
      return "Google Meet";
    case "zoom":
      return "Zoom";
    case "teams":
      return "Microsoft Teams";
    default:
      return platform.charAt(0).toUpperCase() + platform.slice(1);
  }
};

function PlatformIcon({
  platform,
  className,
}: {
  platform: string;
  className?: string;
}) {
  const normalized = platform.toLowerCase();
  if (normalized === "google") {
    return <img src={Google} alt="Google Meet" className={className} />;
  }
  if (normalized === "zoom") {
    return <img src={Zoom} alt="Zoom" className={className} />;
  }
  return <Video className={className} />;
}

function ScheduledCountdown({ target }: { target: Date }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = Math.max(0, target.getTime() - now);
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const label =
    days > 0
      ? `${days}d : ${hours}h : ${minutes}m`
      : `${hours}h : ${minutes}m : ${seconds}s`;

  return (
    <Badge
      variant="outline"
      className="rounded-full border-[#fedf89] bg-[#fffaeb] px-3 py-1 text-sm font-medium text-[#f79009] hover:bg-[#fffaeb] dark:border-[#932F19] dark:bg-[#551C10] dark:text-[#EF6820] dark:hover:bg-[#551C10]"
    >
      Starts in {label}
    </Badge>
  );
}

const formatChartDate = (timestamp: string, frequency: Frequency): string => {
  const date = new Date(timestamp);
  switch (frequency) {
    case "hour":
      return format(date, "HH:mm");
    case "day":
      return format(date, "MMM dd");
    case "month":
      return format(date, "MMM yyyy");
    case "year":
      return format(date, "yyyy");
    default:
      return format(date, "MMM dd");
  }
};

const dateFilterOptions: Array<{
  value: ChartRange["period"];
  label: string;
}> = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Past 24 hours" },
  { value: "week", label: "Past Week" },
  { value: "month", label: "Past Month" },
  { value: "custom", label: "Custom" },
];

function DateRangeFilter() {
  const { chartRange } = Route.useSearch();
  const navigate = Route.useNavigate();

  const [open, setOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    chartRange.period === "custom"
      ? { from: chartRange.from, to: chartRange.to }
      : undefined,
  );

  const triggerLabel =
    chartRange.period === "custom"
      ? `${format(chartRange.from, "MMM d, yyyy")} – ${format(chartRange.to, "MMM d, yyyy")}`
      : chartRange.period === "all"
        ? "Select range"
        : (dateFilterOptions.find((o) => o.value === chartRange.period)
            ?.label ?? "Select range");

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setShowCalendar(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-9 gap-2 rounded-lg border-[#e9eaeb] bg-white px-3 text-sm font-medium text-[#414651] hover:bg-gray-50 dark:border-[#373A41] dark:bg-[#13161B] dark:text-[#CECFD2] dark:hover:bg-[#22262F]"
        >
          <Calendar className="h-4 w-4 text-[#717680] dark:text-[#94979C]" />
          {triggerLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        {!showCalendar ? (
          <div className="flex w-48 flex-col p-1">
            {dateFilterOptions.map((option) => {
              const isSelected = chartRange.period === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={cn(
                    "flex items-center justify-start rounded-md px-3 py-2 text-left text-sm font-medium hover:bg-accent",
                    isSelected && "bg-accent dark:text-white",
                  )}
                  onClick={() => {
                    if (option.value === "custom") {
                      setShowCalendar(true);
                      return;
                    }
                    navigate({
                      to: ".",
                      search: (s) => ({
                        ...s,
                        chartRange: {
                          period: option.value as Exclude<
                            ChartRange["period"],
                            "custom"
                          >,
                        },
                      }),
                    });
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4 p-4">
            <CalendarComponent
              mode="range"
              defaultMonth={
                chartRange.period === "custom" ? chartRange.from : new Date()
              }
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCalendar(false)}
              >
                Back
              </Button>
              <Button
                disabled={!dateRange?.from || !dateRange.to}
                className="flex-1"
                onClick={() => {
                  if (!dateRange?.from || !dateRange.to) return;
                  navigate({
                    to: ".",
                    search: (s) => ({
                      ...s,
                      chartRange: {
                        period: "custom",
                        from: dateRange.from!,
                        to: dateRange.to!,
                      },
                    }),
                  });
                  setOpen(false);
                  setShowCalendar(false);
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function StatusBadge({
  stateGroup,
}: {
  stateGroup: MeetingBot["state_group"];
}) {
  const label = formatStatusLabel(stateGroup);

  if (stateGroup === "ACTIVE") {
    return (
      <span className="inline-flex items-center rounded-full border border-[#FECDCA] bg-[#FEF3F2] px-2.5 py-0.5 text-xs font-medium text-[#D92D20] dark:border-red-400/30 dark:bg-red-500/15 dark:text-red-300">
        <span className="mr-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-[#D92D20] dark:bg-red-400" />
        Live
      </span>
    );
  }

  const styles: Record<string, string> = {
    ENDED:
      "border-[#ABEFC6] bg-[#ECFDF3] text-[#067647] dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-300",
    POST_PROCESSING:
      "border-[#FEDF89] bg-[#FFFAEB] text-[#F79009] dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-300",
    SCHEDULED:
      "border-[#B9E6FE] bg-[#F0F9FF] text-[#026AA2] dark:border-sky-400/30 dark:bg-sky-500/15 dark:text-sky-300",
    PREPARATION:
      "border-[#FEDF89] bg-[#FFFAEB] text-[#F79009] dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-300",
    ERROR:
      "border-[#FECDCA] bg-[#FEF3F2] text-[#D92D20] dark:border-red-400/30 dark:bg-red-500/15 dark:text-red-300",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        styles[stateGroup] ??
          "border-[#E9EAEB] bg-[#FAFAFA] text-[#414651] dark:border-border dark:bg-muted/40 dark:text-foreground",
      )}
    >
      {label}
    </span>
  );
}

function RouteComponent() {
  const { chartRange } = Route.useSearch();

  const { data: meetingsData, isLoading: isLoadingMeetings } = useQuery(
    meetingsListQueryOptions({ per_page: 3 }),
  );

  const recentMeetings = (meetingsData?.items ?? []).slice(0, 3);

  const { data: scheduledData } = useQuery(
    meetingsListQueryOptions({ state_group: ["SCHEDULED"], per_page: 20 }),
  );

  const { data: calendarsData } = useQuery(calendarsQueryOptions());

  const calendarById = new Map<string, LinkedCalendar>();
  for (const cal of calendarsData ?? []) {
    calendarById.set(cal.id, cal);
  }

  const { data: timeSeriesStats, isLoading: isLoadingTimeSeries } = useQuery(
    timeSeriesStatsQueryOptions(chartRange),
  );

  const { data: piiAnalytics, isLoading: isLoadingPiiAnalytics } = useQuery(
    piiAnalyticsStatsQueryOptions(chartRange),
  );

  const { data: riskStats, isLoading: isLoadingRiskStats } = useQuery(
    riskStatsQueryOptions(chartRange),
  );

  const { from, to, frequency } = getDateRangeAndFrequency(chartRange);

  const piiDonutData = [
    {
      name: "Critical",
      value: piiAnalytics?.total_critical ?? 0,
      fill: "#D92D20",
    },
    {
      name: "Confidential",
      value: piiAnalytics?.total_confidential ?? 0,
      fill: "#F79009",
    },
    {
      name: "Normal",
      value: piiAnalytics?.total_standard ?? 0,
      fill: "#099250",
    },
  ];

  const piiTotalCount = piiAnalytics?.total_pii_count ?? 0;

  const piiByType = (piiAnalytics?.pii_summary ?? [])
    .slice()
    .sort((a, b) => b.total_count - a.total_count)
    .map((item) => ({
      name: item.entity_name,
      critical: item.critical_count,
      confidential: item.confidential_count,
      standard: item.standard_count,
      total: item.total_count,
    }));

  const piiChartMax = Math.max(
    1,
    Math.ceil(Math.max(0, ...piiByType.map((item) => item.total)) * 1.05),
  );

  const chartData = (() => {
    const timeSeries = timeSeriesStats?.time_series ?? [];

    if (frequency === "day") {
      const allDays = eachDayOfInterval({ start: from, end: subDays(to, 1) });

      const dataMap = new Map<string, TimeSeriesDataPoint>();
      for (const point of timeSeries) {
        const dateKey = format(new Date(point.timestamp), "yyyy-MM-dd");
        dataMap.set(dateKey, point);
      }

      return allDays.map((day) => {
        const dateKey = format(day, "yyyy-MM-dd");
        const existing = dataMap.get(dateKey);

        if (existing) {
          return {
            ...existing,
            date: formatChartDate(existing.timestamp, frequency),
          };
        }

        return {
          timestamp: day.toISOString(),
          meeting_count: 0,
          pii_count: 0,
          ai_count: 0,
          vexon_fake_count: 0,
          date: formatChartDate(day.toISOString(), frequency),
        };
      });
    }

    return timeSeries.map((point) => ({
      ...point,
      date: formatChartDate(point.timestamp, frequency),
    }));
  })();

  const statsCards = [
    {
      title: "Meetings",
      value: timeSeriesStats?.total_meeting_count ?? 0,
      icon: VideoIcon,
      href: "/meetings" as const,
      isLoading: isLoadingTimeSeries,
    },
    {
      title: "PIIs Detected",
      value: timeSeriesStats?.total_pii_count ?? 0,
      icon: FileText,
      href: "/" as const,
      isLoading: isLoadingTimeSeries,
      shortTitle: "Detected",
    },
    {
      title: "AI Content Flagged",
      value: timeSeriesStats?.total_ai_count ?? 0,
      icon: Flag,
      href: "/" as const,
      isLoading: isLoadingTimeSeries,
      shortTitle: "Flagged",
    },
    {
      title: "Deepfake Voice",
      value: timeSeriesStats?.total_vexon_fake_count ?? 0,
      icon: Volume2,
      href: "/" as const,
      isLoading: isLoadingTimeSeries,
      shortTitle: "Voice",
    },
  ];

  const liveMeeting = recentMeetings.find((m) => m.state_group === "ACTIVE");

  const nextScheduledMeeting = (scheduledData?.items ?? [])
    .map(
      (
        meeting: MeetingBot,
      ): { meeting: MeetingBot; startsAt: string | null } => ({
        meeting,
        startsAt: meeting.join_at ?? meeting.meeting_start_time,
      }),
    )
    .filter(
      (entry: {
        meeting: MeetingBot;
        startsAt: string | null;
      }): entry is ScheduledMeetingEntry =>
        entry.startsAt != null &&
        new Date(entry.startsAt).getTime() > Date.now(),
    )
    .sort(
      (a: ScheduledMeetingEntry, b: ScheduledMeetingEntry) =>
        new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
    )
    .at(0);

  const liveMeetingAiPct =
    liveMeeting?.analytics != null
      ? (() => {
          const ai = liveMeeting.analytics.ai_content_detection.ai;
          const human = liveMeeting.analytics.ai_content_detection.human;
          return ai != null && human != null && ai + human > 0
            ? Math.round((ai / (ai + human)) * 100)
            : null;
        })()
      : null;

  const totalMeetingsCount = riskStats?.total_meetings ?? 0;

  const riskChartData = [
    {
      risk: "high",
      count: riskStats?.high_risk_meetings ?? 0,
      fill: "#D92D20",
    },
    {
      risk: "medium",
      count: riskStats?.medium_risk_meetings ?? 0,
      fill: "#F79009",
    },
    {
      risk: "low",
      count: riskStats?.low_risk_meetings ?? 0,
      fill: "#099250",
    },
  ];

  // const getSeverityBadgeStyles = (severity: string) => {
  //   switch (severity) {
  //     case "Critical":
  //       return "bg-red-50 text-red-600 border-red-200 hover:bg-red-50";
  //     case "High":
  //       return "bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-50";
  //     case "Medium":
  //       return "bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-50";
  //     default:
  //       return "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-50";
  //   }
  // };

  // const getVoiceBadgeStyles = (voiceStatus: string) => {
  //   switch (voiceStatus) {
  //     case "Real":
  //       return "border-emerald-400 bg-emerald-500 text-white";
  //     case "Fake":
  //       return "border-red-400 bg-red-500 text-white";
  //     default:
  //       return "border-gray-400 bg-gray-500 text-white";
  //   }
  // };

  return (
    <div className="w-full">
      <div className="mb-4 flex items-center justify-between sm:mb-6">
        <h1 className="shrink-0 text-2xl leading-8 font-semibold text-[#181D27] dark:text-foreground">
          Dashboard
        </h1>
        <DateRangeFilter />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 sm:gap-6">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.title}
              to={stat.href}
              className="group block h-full"
            >
              <Card className="relative h-full cursor-pointer overflow-hidden border transition-all hover:border-[#FCCEEE] hover:bg-[#FDF2FA] hover:shadow-lg dark:hover:bg-[#FDF2FA]/10">
                <Icon className="pointer-events-none absolute -top-3 -right-7 h-20 w-20 text-[#FAFAFA] group-hover:text-[#FCE7F6] dark:text-white/6 dark:group-hover:text-primary/15" />
                <span className="pointer-events-none absolute bottom-2 -left-5 text-5xl font-black text-[#FAFAFA] transition-colors select-none group-hover:text-[#FCE7F6] dark:text-white/6 dark:group-hover:text-primary/15">
                  {stat.shortTitle ?? stat.title}
                </span>
                <CardHeader className="relative z-10 pb-2">
                  <div className="flex items-center justify-between">
                    <div className="text-3xl font-normal text-[#DD2590] dark:text-[#94979C]">
                      {stat.isLoading ? (
                        <Skeleton className="h-9 w-16" />
                      ) : (
                        stat.value.toLocaleString()
                      )}
                    </div>
                    <div className="rounded-lg p-2">
                      <Icon className="h-7 w-7 text-[#535862] transition-colors group-hover:text-primary dark:text-[#94979C] dark:group-hover:text-primary" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-lg font-normal text-[#414651] transition-colors group-hover:text-[#DD2590] dark:text-[#CECFD2] dark:group-hover:text-primary">
                    {stat.title}
                  </p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Live & Next Scheduled Meeting */}
      <div
        className={cn(
          "mt-4 grid grid-cols-1 gap-4 sm:mt-6 sm:gap-6",
          liveMeeting && nextScheduledMeeting ? "sm:grid-cols-2" : "",
        )}
      >
        {
          liveMeeting ? (
            <Card className="overflow-hidden rounded-[10px] border border-[#f97066] py-0 dark:border-[#D92D20]">
              <CardContent className="flex h-full flex-col justify-between px-5 py-5">
                <div className="flex flex-col gap-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#f04438] opacity-75" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#f04438]" />
                      </span>
                      <span className="text-base font-medium text-[#f04438] dark:text-[#F97066]">
                        Live
                      </span>
                    </div>
                    <Link
                      to="/meetings/$meetingId"
                      params={{ meetingId: liveMeeting.id }}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 rounded-lg border-[#faa7e0] bg-white px-3 py-2 text-sm font-medium text-[#c11574] hover:bg-primary/10 dark:border-[#C11574] dark:bg-transparent dark:text-[#F670C7] dark:hover:bg-primary/10"
                      >
                        Go to Meeting
                      </Button>
                    </Link>
                  </div>

                  <p className="text-base font-medium text-[#414651] dark:text-white">
                    {liveMeeting.meeting_name}
                  </p>

                  <div className="flex flex-wrap items-center gap-2 text-sm text-[#4a5565]">
                    <Clock className="h-4 w-4" />
                    <span className="dark:text-[#CECFD2]">
                      {liveMeeting.meeting_start_time
                        ? format(
                            new Date(liveMeeting.meeting_start_time),
                            "MMM d, yyyy, h:mm a",
                          )
                        : liveMeeting.join_at
                          ? format(
                              new Date(liveMeeting.join_at),
                              "MMM d, yyyy, h:mm a",
                            )
                          : "--"}
                    </span>
                    <span className="text-[#717680] dark:text-[#CECFD2]">
                      •
                    </span>
                    <span className="dark:text-[#CECFD2]">
                      Bot: {liveMeeting.bot_name}
                    </span>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Badge
                    variant="outline"
                    className={cn(
                      "rounded-full px-3 py-1 text-sm font-medium",
                      liveMeetingAiPct != null
                        ? "border-[#FECDCA] bg-[#FEF3F2] text-[#D92D20] hover:bg-[#FEF3F2] dark:border-[#912018] dark:bg-[#55160C] dark:text-[#F97066] dark:hover:bg-[#55160C]"
                        : "border-[#E9EAEB] bg-[#FAFAFA] text-[#414651] hover:bg-[#FAFAFA] dark:border-[#373A41] dark:bg-[#13161B] dark:text-[#CECFD2] dark:hover:bg-[#13161B]",
                    )}
                  >
                    {liveMeetingAiPct ?? 0}% AI
                  </Badge>
                  <Badge
                    variant="outline"
                    className={cn(
                      "rounded-full px-3 py-1 text-sm font-medium hover:bg-[#FFFAEB]",
                      PII_BADGE_CLASSES,
                    )}
                  >
                    {liveMeeting.analytics?.pii.count ?? 0} PII
                  </Badge>
                  <Badge
                    variant="outline"
                    className={cn(
                      "rounded-full py-1 pr-3 pl-2.5 text-sm font-medium",
                      liveMeeting.analytics?.voice_authentication.fake_ratio !=
                        null
                        ? "border-[#ABEFC6] bg-[#ECFDF3] text-[#067647] hover:bg-[#ECFDF3] dark:border-[#085D3A] dark:bg-[#053321] dark:text-[#75E0A7] dark:hover:bg-[#053321]"
                        : "border-[#E9EAEB] bg-[#FAFAFA] text-[#414651] hover:bg-[#FAFAFA] dark:border-[#373A41] dark:bg-[#13161B] dark:text-[#CECFD2] dark:hover:bg-[#13161B]",
                    )}
                  >
                    <Volume2 className="mr-1 size-3.5" />
                    {liveMeeting.analytics?.voice_authentication.fake_ratio !=
                    null
                      ? `${Math.round(liveMeeting.analytics.voice_authentication.fake_ratio)}%`
                      : "0%"}
                  </Badge>
                  <Badge
                    variant="outline"
                    className="rounded-full border-[#e9eaeb] bg-[#fafafa] py-1 pr-3 pl-2.5 text-sm font-medium text-[#414651] hover:bg-[#fafafa] dark:border-[#373A41] dark:bg-[#13161B] dark:text-[#CECFD2] dark:hover:bg-[#13161B]"
                  >
                    <PlatformIcon
                      platform={liveMeeting.platform}
                      className="mr-1 size-3.5"
                    />
                    {formatPlatform(liveMeeting.platform)}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ) : (
            ""
          )
          // (
          //   <Card className="overflow-hidden rounded-[10px] border border-gray-200 shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]">
          //     <CardContent className="flex h-full items-center justify-center p-5">
          //       <div className="text-center">
          //         <Video className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
          //         <p className="text-sm text-muted-foreground">
          //           No live meetings right now
          //         </p>
          //       </div>
          //     </CardContent>
          //   </Card>
          // )
        }

        {nextScheduledMeeting ? (
          <Card className="overflow-hidden rounded-[10px] border border-[#e9eaeb] py-0 dark:border-[#22262F]">
            <CardContent className="flex h-full flex-col justify-between px-5 py-5">
              <div className="flex flex-col gap-3">
                <div className="flex items-start justify-between">
                  <ScheduledCountdown
                    target={new Date(nextScheduledMeeting.startsAt)}
                  />
                  <Link
                    to="/meetings/$meetingId"
                    params={{ meetingId: nextScheduledMeeting.meeting.id }}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 rounded-lg border-[#faa7e0] bg-white px-3 py-2 text-sm font-medium text-[#c11574] hover:bg-primary/10 dark:border-[#C11574] dark:bg-transparent dark:text-[#F670C7] dark:hover:bg-primary/10"
                    >
                      Go to Meeting
                    </Button>
                  </Link>
                </div>

                <p className="text-base font-medium text-[#414651] dark:text-white">
                  {nextScheduledMeeting.meeting.meeting_name}
                </p>

                <div className="flex flex-wrap items-center gap-2 text-sm text-[#4a5565] dark:text-[#CECFD2]">
                  <Clock className="h-4 w-4" />
                  <span className="dark:text-[#CECFD2]">
                    {format(
                      new Date(nextScheduledMeeting.startsAt),
                      "MMM d, yyyy, h:mm a",
                    )}
                  </span>
                  <span className="text-[#717680] dark:text-[#717680]">•</span>
                  <span className="dark:text-[#717680]">
                    Bot: {nextScheduledMeeting.meeting.bot_name}
                  </span>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Badge
                  variant="outline"
                  className="rounded-full border-[#e9eaeb] bg-[#fafafa] py-1 pr-3 pl-2.5 text-sm font-medium text-[#414651] hover:bg-[#fafafa] dark:border-[#373A41] dark:bg-[#13161B] dark:text-[#CECFD2] dark:hover:bg-[#13161B]"
                >
                  <PlatformIcon
                    platform={nextScheduledMeeting.meeting.platform}
                    className="mr-1 size-3.5"
                  />
                  {formatPlatform(nextScheduledMeeting.meeting.platform)}
                </Badge>
                <MeetingCalendarBadge
                  platform={
                    nextScheduledMeeting.meeting.calendar_id
                      ? calendarById.get(
                          nextScheduledMeeting.meeting.calendar_id,
                        )?.platform
                      : undefined
                  }
                />
              </div>
            </CardContent>
          </Card>
        ) : (
          ""
        )}
      </div>

      <div className="mt-4 grid w-full gap-4 sm:mt-6 sm:gap-6">
        <Card className="overflow-hidden">
          <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-2">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <CardTitle className="text-base font-semibold sm:text-lg">
                Detection Trend
              </CardTitle>
              {/* <span className="text-xs font-medium text-[#099250] sm:text-sm">
                ↑ 15% safer than your 30-day average
              </span> */}
            </div>
          </CardHeader>
          <CardContent className="p-2 pt-4 sm:p-6 sm:pt-4">
            {isLoadingTimeSeries ? (
              <Skeleton className="h-[300px] w-full sm:h-[360px]" />
            ) : chartData.length === 0 ? (
              <div className="flex h-[300px] flex-col items-center justify-center sm:h-[360px]">
                <p className="text-sm text-muted-foreground">
                  No data available for the selected period
                </p>
              </div>
            ) : (
              <ChartContainer
                config={chartConfig}
                className="h-[300px] w-full sm:h-[360px]"
              >
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 20, left: -15, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={true}
                    vertical={false}
                    stroke="#e5e7eb"
                  />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    tickFormatter={(value) => value.toString()}
                    domain={[0, "auto"]}
                  />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Line
                    type="monotone"
                    dataKey="meeting_count"
                    stroke="var(--color-meeting_count)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="vexon_fake_count"
                    stroke="var(--color-vexon_fake_count)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="ai_count"
                    stroke="var(--color-ai_count)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="pii_count"
                    stroke="var(--color-pii_count)"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ChartContainer>
            )}
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Time
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              <div className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#4E5BA6]" />
                <span className="text-sm text-muted-foreground">Meetings</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#0BA5EC]" />
                <span className="text-sm text-muted-foreground">Deepfake</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#9B8AFB]" />
                <span className="text-sm text-muted-foreground">
                  AI Content
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#E478FA]" />
                <span className="text-sm text-muted-foreground">
                  PII Detected
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PII Detected + Top Threat Users */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:mt-6 sm:gap-6 lg:grid-cols-1">
        <Card className="overflow-hidden">
          <CardContent>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-foreground">
                PII Detected
              </h3>
              {/* <Link
                to="/analytics"
                className="text-sm font-medium text-pink-500 transition-colors hover:text-pink-600"
              >
                See all
              </Link> */}
            </div>

            <div className="mt-4 flex flex-col items-center gap-6 sm:flex-row sm:gap-10">
              <div className="flex shrink-0 flex-col items-center gap-4">
                <div className="relative h-[120px] w-[120px]">
                  {isLoadingPiiAnalytics ? (
                    <Skeleton className="h-full w-full rounded-full" />
                  ) : (
                    <>
                      <ChartContainer
                        config={{
                          confidential: {
                            label: "Confidential",
                            color: "#F79009",
                          },
                          critical: {
                            label: "Critical",
                            color: "#D92D20",
                          },
                          standard: {
                            label: "Normal",
                            color: "#099250",
                          },
                        }}
                        className="h-full w-full"
                      >
                        <PieChart>
                          <Pie
                            data={piiDonutData}
                            cx="50%"
                            cy="50%"
                            innerRadius={45}
                            outerRadius={60}
                            startAngle={90}
                            endAngle={-270}
                            paddingAngle={1}
                            cornerRadius={2}
                            dataKey="value"
                            strokeWidth={0}
                            activeShape={false}
                          >
                            {piiDonutData.map((entry) => (
                              <Cell key={entry.name} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip
                            cursor={false}
                            wrapperStyle={{ zIndex: 20, outline: "none" }}
                            content={<PiiBarTooltip />}
                          />
                        </PieChart>
                      </ChartContainer>
                      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                        <span
                          className={cn(
                            "font-semibold text-foreground tabular-nums",
                            piiTotalCount >= 1000 ? "text-lg" : "text-xl",
                          )}
                        >
                          {piiTotalCount >= 10000
                            ? Intl.NumberFormat("en", {
                                notation: "compact",
                                maximumFractionDigits: 1,
                              }).format(piiTotalCount)
                            : piiTotalCount.toLocaleString()}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex flex-col items-start gap-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: "#D92D20" }}
                    />
                    <span className="text-sm text-muted-foreground">
                      Critical
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: "#F79009" }}
                    />
                    <span className="text-sm text-muted-foreground">
                      Confidential
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: "#099250" }}
                    />
                    <span className="text-sm text-muted-foreground">
                      Normal
                    </span>
                  </div>
                </div>
              </div>

              <div className="w-full min-w-0 flex-1">
                {isLoadingPiiAnalytics ? (
                  <Skeleton className="h-[200px] w-full sm:h-[180px]" />
                ) : piiByType.length === 0 ? (
                  <div className="flex h-[200px] w-full items-center justify-center sm:h-[180px]">
                    <p className="text-sm text-muted-foreground">
                      No PII detected for the selected period
                    </p>
                  </div>
                ) : (
                  <div className="flex h-[200px] w-full min-w-0 sm:h-[180px]">
                    <ChartContainer
                      config={piiCriticalityChartConfig}
                      className="pointer-events-none h-full w-12 shrink-0"
                    >
                      <BarChart
                        data={piiByType}
                        margin={{ top: 4, right: 0, left: 0, bottom: 45 }}
                      >
                        <XAxis dataKey="name" hide height={50} />
                        <YAxis
                          width={48}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 11, fill: "#9ca3af" }}
                          domain={[0, piiChartMax]}
                          allowDecimals={false}
                        />
                      </BarChart>
                    </ChartContainer>

                    <div className="min-w-0 flex-1 overflow-x-auto">
                      <ChartContainer
                        config={piiCriticalityChartConfig}
                        style={{
                          minWidth: Math.max(piiByType.length * 56 + 48, 100),
                        }}
                        className="h-full w-full"
                      >
                        <BarChart
                          data={piiByType}
                          margin={{
                            top: 4,
                            right: 0,
                            left: 36,
                            bottom: 45,
                          }}
                          barSize={30}
                        >
                          <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={<PiiBarTick angled />}
                            height={50}
                            interval={0}
                          />
                          <YAxis
                            hide
                            width={0}
                            domain={[0, piiChartMax]}
                            allowDecimals={false}
                          />
                          <CartesianGrid
                            horizontal={true}
                            vertical={false}
                            stroke="#e5e7eb"
                          />
                          <Tooltip
                            cursor={{ fill: "var(--muted)", radius: 4 }}
                            wrapperStyle={{ outline: "none" }}
                            content={<ChartTooltipContent />}
                          />
                          <Bar
                            dataKey="standard"
                            stackId="pii"
                            fill="var(--color-standard)"
                            stroke="var(--background)"
                            strokeWidth={2}
                          >
                            {piiByType.map((entry) => (
                              <Cell
                                key={`standard-${entry.name}`}
                                radius={getPiiStackRadius(entry).standard}
                              />
                            ))}
                          </Bar>
                          <Bar
                            dataKey="confidential"
                            stackId="pii"
                            fill="var(--color-confidential)"
                            stroke="var(--background)"
                            strokeWidth={2}
                          >
                            {piiByType.map((entry) => (
                              <Cell
                                key={`confidential-${entry.name}`}
                                radius={getPiiStackRadius(entry).confidential}
                              />
                            ))}
                          </Bar>
                          <Bar
                            dataKey="critical"
                            stackId="pii"
                            fill="var(--color-critical)"
                            stroke="var(--background)"
                            strokeWidth={2}
                          >
                            {piiByType.map((entry) => (
                              <Cell
                                key={`critical-${entry.name}`}
                                radius={getPiiStackRadius(entry).critical}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ChartContainer>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Meetings Risk + Meetings list */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:mt-6 sm:gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardContent className="flex h-full flex-col">
            <h3 className="mb-4 text-lg font-medium text-foreground">
              Meetings
            </h3>
            <div className="flex flex-1 flex-wrap items-center justify-center gap-4 sm:gap-6">
              <div className="relative h-36 w-36 shrink-0 sm:h-44 sm:w-44">
                {isLoadingRiskStats ? (
                  <Skeleton className="h-full w-full rounded-full" />
                ) : (
                  <>
                    <ChartContainer
                      config={riskChartConfig}
                      className="h-full w-full"
                    >
                      <RadialBarChart
                        data={riskChartData}
                        innerRadius="46%"
                        outerRadius="100%"
                        barSize={10}
                        startAngle={90}
                        endAngle={-270}
                        cx="50%"
                        cy="50%"
                      >
                        <PolarAngleAxis
                          type="number"
                          domain={[0, totalMeetingsCount || 1]}
                          angleAxisId={0}
                          tick={false}
                        />
                        <Tooltip
                          shared={false}
                          cursor={false}
                          wrapperStyle={{ zIndex: 20, outline: "none" }}
                          content={
                            <ChartTooltipContent
                              hideLabel
                              nameKey="risk"
                              className="border-[#0C111D] bg-[#0C111D] text-white dark:border-white dark:bg-white dark:text-[#0C111D] [&_.text-foreground]:text-white dark:[&_.text-foreground]:text-[#0C111D] [&_.text-muted-foreground]:text-white dark:[&_.text-muted-foreground]:text-[#0C111D]"
                            />
                          }
                        />
                        <RadialBar
                          dataKey="count"
                          cornerRadius={5}
                          background={{
                            fill: "#f3f4f6",
                            pointerEvents: "none",
                          }}
                          angleAxisId={0}
                        />
                      </RadialBarChart>
                    </ChartContainer>
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                      <span
                        className={cn(
                          "font-semibold text-foreground tabular-nums",
                          totalMeetingsCount >= 1000
                            ? "text-lg sm:text-xl"
                            : "text-xl sm:text-2xl",
                        )}
                      >
                        {totalMeetingsCount >= 10000
                          ? Intl.NumberFormat("en", {
                              notation: "compact",
                              maximumFractionDigits: 1,
                            }).format(totalMeetingsCount)
                          : totalMeetingsCount.toLocaleString()}
                      </span>
                    </div>
                  </>
                )}
              </div>
              <div className="flex shrink-0 flex-col gap-3">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 shrink-0 rounded-full bg-[#099250]" />
                  <span className="text-sm whitespace-nowrap text-muted-foreground">
                    Low risk
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 shrink-0 rounded-full bg-[#F79009]" />
                  <span className="text-sm whitespace-nowrap text-muted-foreground">
                    Medium risk
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 shrink-0 rounded-full bg-[#D92D20]" />
                  <span className="text-sm whitespace-nowrap text-muted-foreground">
                    High risk
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="flex h-full flex-col">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                Meetings list
              </h3>
              <Link
                to="/meetings"
                className="shrink-0 text-sm font-medium text-pink-500 transition-colors hover:text-pink-600"
              >
                See all
              </Link>
            </div>
            {isLoadingMeetings ? (
              <Skeleton className="h-48 w-full" />
            ) : recentMeetings.length === 0 ? (
              <div className="flex h-48 flex-1 flex-col items-center justify-center text-center">
                <p className="text-sm text-muted-foreground">
                  No meetings found
                </p>
              </div>
            ) : (
              <div className="flex flex-1 flex-col divide-y divide-border">
                {recentMeetings.map((meeting) => {
                  const severity = getMeetingSeverity(meeting);
                  const analytics = meeting.analytics;
                  const aiCounts = analytics?.ai_content_detection;
                  const aiPercentage =
                    aiCounts?.ai != null &&
                    aiCounts.human != null &&
                    (aiCounts.ai !== 0 || aiCounts.human !== 0)
                      ? Math.round(
                          (aiCounts.ai / (aiCounts.ai + aiCounts.human)) * 100,
                        )
                      : null;
                  const fakeRatio =
                    analytics?.voice_authentication.fake_ratio ?? null;
                  const showAiBadge = aiPercentage !== null && aiPercentage > 0;
                  const showFakeBadge = fakeRatio != null && fakeRatio > 0;
                  const showPiiBadge = (analytics?.pii.count ?? 0) > 0;
                  const hasNoThreats =
                    !showAiBadge && !showFakeBadge && !showPiiBadge;

                  const duration = formatMeetingDuration(meeting);
                  const platformLabel = formatMeetingPlatform(meeting.platform);

                  return (
                    <Link
                      key={meeting.id}
                      to="/meetings/$meetingId"
                      params={{ meetingId: meeting.id }}
                      className="flex items-center gap-4 py-4 sm:gap-5"
                    >
                      <div className="group/col -my-2 -ml-3 flex min-w-0 flex-1 items-center gap-4 rounded-lg py-2 pl-3 transition-colors hover:bg-muted/50 sm:gap-5">
                        <UiTooltipProvider delayDuration={0}>
                          <UiTooltip>
                            <UiTooltipTrigger asChild>
                              <span className="-mx-[5px] flex h-10 w-4 shrink-0 items-center justify-center">
                                <span
                                  className={cn(
                                    "h-10 w-[5px] rounded-full transition-transform duration-200 group-hover/col:scale-125",
                                    getSeverityAccentClass(severity),
                                  )}
                                />
                              </span>
                            </UiTooltipTrigger>
                            <UiTooltipContent
                              side="right"
                              sideOffset={8}
                              className="rounded-lg bg-[#0C111D] px-3 py-2 text-sm font-semibold text-white shadow-lg dark:bg-white dark:text-[#0C111D]"
                            >
                              {getSeverityLabel(severity)}
                            </UiTooltipContent>
                          </UiTooltip>
                        </UiTooltipProvider>

                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <h4 className="truncate text-[15px] font-medium text-foreground group-hover/col:text-[#DD2590]">
                            {meeting.meeting_name}
                          </h4>
                          <span className="text-sm text-muted-foreground">
                            {platformLabel}
                            {duration ? ` \u2022 ${duration}` : ""}
                          </span>
                        </div>
                      </div>

                      <div className="hidden flex-wrap items-center gap-1.5 sm:flex">
                        {hasNoThreats ? (
                          <Badge
                            variant="outline"
                            className="rounded-full border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-border dark:bg-muted/40 dark:text-muted-foreground dark:hover:bg-muted/40"
                          >
                            No threats
                          </Badge>
                        ) : (
                          <>
                            {showAiBadge && (
                              <Badge
                                variant="outline"
                                className="rounded-full border-[#E9EAEB] bg-[#FAFAFA] px-2.5 py-1 text-xs font-medium text-[#414651] hover:bg-[#FAFAFA] dark:border-border dark:bg-muted/40 dark:text-foreground dark:hover:bg-muted/40"
                              >
                                <span className="text-[#DD2590]">
                                  {aiPercentage}%
                                </span>
                                &nbsp;AI
                              </Badge>
                            )}
                            {showPiiBadge && (
                              <Badge
                                variant="outline"
                                className={cn(
                                  "rounded-full px-2.5 py-1 text-xs font-medium hover:bg-[#FFFAEB]",
                                  PII_BADGE_CLASSES,
                                )}
                              >
                                {analytics?.pii.count ?? 0} PII
                              </Badge>
                            )}
                            {showFakeBadge && (
                              <Badge
                                variant="outline"
                                className="rounded-full border-[#E9EAEB] bg-[#FAFAFA] px-2.5 py-1 text-xs font-medium text-[#414651] hover:bg-[#FAFAFA] dark:border-border dark:bg-muted/40 dark:text-foreground dark:hover:bg-muted/40"
                              >
                                <Volume2 className="mr-1 h-3 w-3" />
                                {Math.round(fakeRatio)}%
                              </Badge>
                            )}
                          </>
                        )}
                      </div>

                      <div className="shrink-0">
                        <StatusBadge stateGroup={meeting.state_group} />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
